<template>
  <div
    class="pa-6 droparea"
    style="min-height: 500px"
    @dragenter.prevent="dragenter"
  >
    <v-overlay
      color="white"
      light
      :value="fileUpload.isDragging"
      :opacity="0.8"
      @dragleave.prevent="dragleave"
      @dragover.prevent="dragover"
      @drop.prevent="drop"
    >
      <div
        class="mb-4 text-center"
        style="pointer-events: none"
      >
        <v-icon
          x-large
          color="grey darken-4"
        >
          fal fa-upload
        </v-icon>
      </div>
      <div
        class="grey--text text--darken-4"
        style="pointer-events: none"
      >
        {{ $t('card_title.release_to_upload') }}
      </div>
    </v-overlay>

    <v-row
      v-if="$can('create-event-media')"
      align="center"
      class="mb-4"
    >
      <v-col class="text-body-2 grey--text">
        {{ $t('media_upload.instruction') }}
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="grey darken-2"
          text
          class="text-none"
          @click="$refs.uploadFile.click()"
        >
          {{ $t('button.browse') }}
          <v-icon
            right
            small
          >
            fal fa-upload
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <input
      id="upload-file"
      ref="uploadFile"
      type="file"
      multiple
      style="display: none"
      accept="video/*,audio/*,image/*,.doc,.docx,.pptx,.xlsx,.xls,application/pdf,application/octet-stream,.dkg,.csv"
      @input="handleFileChange"
    >

    <div>
      <v-row>
        <v-col
          v-for="(mediaItem, mediaItemIndex) in media"
          :key="mediaItem.id"
          cols="6"
        >
          <media-grid-item
            :media-item="mediaItem"
            :can-delete="$can('delete-event-media')"
            @preview="preview"
            @delete="mediaItem.id ? startDelete(mediaItem) : media.splice(mediaItemIndex, 1)"
          />
        </v-col>
      </v-row>
    </div>

    <media-viewer-dialog
      :visible.sync="mediaViewerDialog.visible"
      :media="media"
      :view-media-id="mediaViewerDialog.media ? mediaViewerDialog.media.id : undefined"
      :view-only="!$can('edit-event-media')"
      :can-delete="$can('delete-event-media')"
      @media:updated="refreshEvent"
      @media:deleted="refreshEvent"
    />

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="destroy()"
    />
  </div>
</template>

<script>
import _axios from '@/plugins/axios'
import { cloneDeep } from 'lodash'
import { MediaApiService } from '@/data/services/MediaApiService'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import MediaViewerDialog from '@/components/dialogs/MediaViewerDialog.vue'
import MediaGridItem from '@/components/helpers/MediaGridItem.vue'

export default {
  name: 'EventMedia',
  components: { MediaGridItem, MediaViewerDialog, DeleteConfirmationDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    fileUpload: {
      isDragging: false
    },
    media: [],
    mediaViewerDialog: {
      visible: false,
      media: undefined
    },
    deleteMedia: undefined,
    deleting: false
  }),
  watch: {
    event: function () {
      this.media = cloneDeep(this.event.media)
    }
  },
  created() {
    this.media = cloneDeep(this.event.media)

    const this2 = this

    this.keyListener = (event) => {
      if (event.key === 'ArrowRight') {
        this2.nextMedia()
      } else if (event.key === 'ArrowLeft') {
        this2.previousMedia()
      }
    }

    window.addEventListener('keyup', this.keyListener)
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.keyListener)
  },
  methods: {
    async handleFileChange() {
      await this.$nextTick()

      const files = [...this.$refs.uploadFile.files]

      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'dkg', 'csv']

      for (const file of files) {
        const type = file.type
        const extension = file.name.split('.').pop()

        if (type.match('image.*') || type.match('video.*') || type.match('audio.*') || allowedExtensions.indexOf(extension) > -1) {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            upload: true
          })
        } else {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            error: 'File type not allowed'
          })
        }
      }

      this.processUpload()
    },
    async processUpload() {
      let formData

      for (const file of this.media.filter(m => !!m.upload)) {
        formData = new FormData()
        formData.append('file', file.file, file.name)

        _axios.post(`events/${this.event.id}/media-upload`, formData, {
          onUploadProgress: (e) => {
            file.progress = Number((e.loaded / e.total) * 100)
          }
        }).then(response => {
          file.upload = false

          Object.assign(file, response.data.media)

          this.$emit('update:media', this.media)
        }).catch(error => {
          if (error.response && error.response.status === 422) {
            this.$store.dispatch(this.$storeActions.ALERT, {
              color: 'error',
              text: this.$t('alert.file_type_not_allowed.error')
            })

            file.error = this.$t('alert.file_type_not_allowed.error')

            return
          }

          this.$store.dispatch(this.$storeActions.ALERT, {
            color: 'error',
            text: this.$t('alert.uploading_file.error')
          })

          file.error = this.$t('alert.uploading_file.error')
        })
      }
    },
    preview(media) {
      this.mediaViewerDialog.visible = true
      this.mediaViewerDialog.media = media
    },
    dragover(e) {
      this.fileUpload.isDragging = true
    },
    dragleave(e) {
      this.fileUpload.isDragging = false
    },
    dragenter(e) {
      this.fileUpload.isDragging = true
    },
    drop(e) {
      this.$refs.uploadFile.files = e.dataTransfer.files
      this.handleFileChange()
      this.fileUpload.isDragging = false
    },
    startDelete(media) {
      this.deleteMedia = media
      this.deleteConfirmationDialogVisible = true
    },
    async destroy() {
      if (!this.deleteMedia) return

      const id = this.deleteMedia.id

      try {
        this.deleting = true

        await MediaApiService.delete(id)

        const index = this.media.findIndex(m => m.id === id)

        if (index > -1) this.media.splice(index, 1)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        this.deleteMedia = undefined

        this.$emit('update')
      } catch (e) {

      } finally {
        this.deleting = false
      }
    },
    refreshEvent() {
      this.$emit('update')
    }
  }
}
</script>
